import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import ImageUpload from '../../components/ui/ImageUpload';
import { categoriesAPI } from '../../services/api';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' ? { slug: value.toLowerCase().replace(/\s+/g, '-') } : {})
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await categoriesAPI.update(editingId, formData);
        setCategories(categories.map(c =>
          c.id === editingId ? { ...c, ...formData } : c
        ));
      } else {
        const response = await categoriesAPI.create(formData);
        setCategories([...categories, response.data]);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image: category.image || ''
    });
    setShowNewForm(true);
  };

  const handleDelete = async () => {
    try {
      await categoriesAPI.delete(deleteId);
      setCategories(categories.filter(c => c.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '', image: '' });
    setEditingId(null);
    setShowNewForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Categories</h1>
          <p className="text-zinc-400">{categories.length} categories</p>
        </div>
        {!showNewForm && (
          <Button
            onClick={() => setShowNewForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        )}
      </div>

      {/* New/Edit Form */}
      {showNewForm && (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">
              {editingId ? 'Edit Category' : 'New Category'}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={resetForm}
              className="text-zinc-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white">Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Slug</Label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-white">Description</Label>
            <Input
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          <div className="mb-6">
            <Label className="text-white">Image</Label>
            <div className="mt-2">
              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={resetForm}
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingId ? 'Save Changes' : 'Create Category'}
            </Button>
          </div>
        </div>
      )}

      {/* Categories list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 flex items-center gap-4"
          >
            {category.image && (
              <img
                src={category.image}
                alt={category.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium">{category.name}</h3>
              <p className="text-zinc-400 text-sm">{category.slug}</p>
              {category.description && (
                <p className="text-zinc-500 text-sm truncate">{category.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="border-zinc-700 text-zinc-400 hover:text-white"
                onClick={() => handleEdit(category)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-zinc-700 text-red-400 hover:text-red-300 hover:border-red-500/50"
                onClick={() => setDeleteId(category.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Category</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this category? Products in this category will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesManager;
